import { motion, AnimatePresence, useReducedMotion } from "framer-motion"
import { useState, useEffect, useMemo } from "react"
import "./Smartsection.css"
import "./Smartanimation.css"

import heroPhone1 from "../../../assets/hero-phone1.png"
import heroPhone2 from "../../../assets/hero-phone2.png"
import heroPhone3 from "../../../assets/hero-phone3.png"
import heroPhone4 from "../../../assets/hero-phone4.png"
import heroPhone5 from "../../../assets/hero-phone5.png"
import heroPhone6 from "../../../assets/hero-phone6.png"
import heroPhone7 from "../../../assets/hero-phone7.png"
import heroPhone8 from "../../../assets/hero-phone8.png"
import heroCard1 from "../../../assets/hero-card1.png"
import heroCard2 from "../../../assets/hero-card2.png"
import heroCard3 from "../../../assets/hero-card3.png"
import heroCard4 from "../../../assets/hero-card4.png"
import heroCard5 from "../../../assets/hero-card5.png"
import heroCard6 from "../../../assets/hero-card6.png"
import heroCard7 from "../../../assets/hero-card7.png"

const Smartsection = () => {
  const [step, setStep] = useState(0)
  const prefersReducedMotion = useReducedMotion()

  // All phone screens
  const phones = useMemo(
    () => [heroPhone1, heroPhone2, heroPhone3, heroPhone4, heroPhone5, heroPhone6, heroPhone7, heroPhone8],
    [],
  )

  // --- TIMING ---
  const TOTAL_STEPS = 15 // total steps
  const STEP_MS = 1200 // step duration
  const GAP_MS = 2000 // extra pause at end

  useEffect(() => {
    if (prefersReducedMotion) return

    let timer;

    const tick = (current) => {
      const next = (current + 1) % TOTAL_STEPS
      setStep(next)

      // if we're looping back to 0, pause longer
      const delay = next === TOTAL_STEPS - 1 ? GAP_MS : STEP_MS
      timer = setTimeout(() => tick(next), delay)
    }

    // start cycle
    timer = setTimeout(() => tick(step), STEP_MS)

    return () => clearTimeout(timer)
  }, [prefersReducedMotion])

  // --- SYNC PHONE WITH CARD ENTRANCES ---
  const phoneAt = useMemo(
    () => [
      [0, 0],  // initial
      [1.2, 1],  // card1 enters
      [3.5, 2],  // card2 enters
      [5.5, 3],  // card3+4 enter
      [7.5, 4],  // card5 enters
      [9.5, 5], // card6 enters
      [11.5, 6], // card7 enters
      [12.5, 7], // final look
    ],
    [],
  )

  const activePhoneIdx = useMemo(() => {
    let idx = 0
    for (const [threshold, phoneIndex] of phoneAt) {
      if (step >= threshold) idx = phoneIndex
      else break
    }
    return idx
  }, [step, phoneAt])

  // Shared transition
  const fadeTransition = { duration: 0.7, ease: "easeInOut" }

  return (
    <div className="SmartContainer">
      <div className="SmartWrapper">
        <div className="TextWrapper">
          <h1>
            Smart NFC Cards 
            <span> Built to Impress Every Connection</span>
          </h1>
          <p>
            {/* Say goodbye to outdated paper cards—go digital with NFC cards and share who you are instantly, anytime, anywhere. */}
            {/* Connect Everywhere Instantly */}
          </p>
        </div>

        {/* Animation Wrapper */}
        <div className="smartImgWrapper">
          {/* Phone screen crossfade (key changes when activePhoneIdx changes) */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.img
              key={`phone-${activePhoneIdx}`}
              src={phones[activePhoneIdx]}
              alt={`phone-${activePhoneIdx + 1}`}
              className="phoneScreen"
              initial={{ opacity: 1, scale: 1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1 }}
              transition={activePhoneIdx > 0 ? fadeTransition : undefined}
            />
          </AnimatePresence>

          {/* Device frame always on top */}
          <img src={phones[activePhoneIdx] || "/placeholder.svg"} alt="phone-frame" className="phoneFrame" />

          {/* Cards */}
          {step >= 1 && (
            <motion.img
              src={heroCard1}
              alt="card1"
              className="heroCard card1"
              initial={{ x: "110%", y: "30%", opacity: 1 }}
              animate={{
                x: ["110%", "12%", "12%", "12%"], // slide in → stay → stay → stay
                y: ["30%", "30%", "26%", "30%"], // stay → stay → up → down
                opacity: 1
              }}
              transition={{
                duration: 2,
                ease: "easeOut",
                times: [0, 0.6, 0.8, 1] // control timing of each keyframe
              }}
            />
          )}

          {step >= 3 && (
            <motion.img
              src={heroCard2}
              alt="card2"
              className="heroCard card2"
              initial={{ x: "0%", y: "200%", opacity: 0, scale: 0.5 }}
              animate={{ x: "-210%", y: "110%", opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
          )}

          {step >= 5 && (
            <>
              <motion.img
                src={heroCard3}
                alt="card3"
                className="heroCard card2"
                initial={{ x: "0%", y: "200%%", opacity: 0, scale: 0.5 }}
                animate={{ x: "-300%", y: "400%", opacity: 1, scale: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
              <motion.img
                src={heroCard4}
                alt="card4"
                className="heroCard card2"
                initial={{ x: "0%", y: "0%", opacity: 0, scale: 0.5 }}
                animate={{ x: "-500%", y: "880%", opacity: 1, scale: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
            </>
          )}

          {step >= 7 && (
            <motion.img
              src={heroCard5}
              alt="card5"
              className="heroCard card5"
              initial={{ x: "0%", y: "440%", opacity: 0, scale: 0.5 }}
              animate={{ x: "100%", y: "440%", opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
          )}

          {step >= 9 && (
            <motion.img
              src={heroCard6}
              alt="card6"
              className="heroCard card5"
              initial={{ x: "0%", y: "900%", opacity: 0, scale: 0.5 }}
              animate={{ x: "80%", y: "1100%", opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
          )}

          {step >= 11 && (
            <motion.img
              src={heroCard7}
              alt="card7"
              className="heroCard card6"
              initial={{ x: "0%", y: "650%", opacity: 0, scale: 0.5 }}
              animate={{ x: "-180%", y: "650%", opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Smartsection